import Dialog from "@mui/material/Dialog";
import { Form, Formik, FormikHelpers } from "formik";
import moment from "moment";
import { useEffect, useRef } from "react";
import { InstallmentPaymentValues } from "src/models/InstallmentPayment.model";
import {
  useGetInstallmentByIdQuery,
  usePayInstallmentMutation,
} from "src/services/InstallmentServices";
import { showToast } from "src/utils/showToaster";
import { object, string } from "yup";
import InstallmentPayment from "./InstallmentPayment";
import ATMFullPageLoader from "src/components/UI/atoms/ATMFullPageLoader/ATMFullPageLoader";
import { useParams } from "react-router-dom";
import { useGetCouponByCodeMutation } from "src/services/CouponServices";

export const getCouponDiscount = (
  amount: string | number,
  discountType: "FIXED" | "PERCENTAGE",
  discountValue: string,
  maxDiscountAmount: string
) => {
  if (discountType === "PERCENTAGE") {
    const discount = Math.min(
      (Number(amount) * Number(discountValue)) / 100,
      Number(maxDiscountAmount)
    );

    return {
      discountAmount: discount,
      discountedAmount: Number(amount) - discount,
    };
  }

  const discount = Math.min(Number(discountValue), Number(maxDiscountAmount));
  return {
    discountAmount: discount,
    discountedAmount: Number(amount) - discount,
  };
};

type Props = {
  onClose: () => void;
  installmentId: string;
};

const InstallmentPaymentWrapper = ({ onClose, installmentId }: Props) => {
  const { studentId = "" } = useParams();

  const [getCouponDetails, { isLoading: isCouponDetailsLoading }] =
    useGetCouponByCodeMutation();

  const { data, isLoading, isFetching } = useGetInstallmentByIdQuery(
    installmentId || ""
  );

  const formikRef = useRef<any>(null);

  const [payInstallment] = usePayInstallmentMutation();

  useEffect(() => {
    if (!isLoading && !isFetching) {
      formikRef.current?.setFieldValue?.(
        "installmentAmount",
        data?.data?.amount
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  // Form Initial Values
  const initialValues: InstallmentPaymentValues = {
    date: new Date(),
    paymentMode: {
      label: "Cash",
      value: "CASH",
    },
    account: "",
    paymentRemark: "",
    installmentAmount: "0",
    netReceivedAmount :'0' ,
    couponCode: "",
    couponData: null,
  };

  // Validation Schema
  const validationSchema = object().shape({
    date: string().required("Please select date of payment"),
    account: object().test(
      "receivedAmtTest",
      "Please select account",
      (value, context) => {
        const { netReceivedAmount } = context.parent;
        if (netReceivedAmount === 0) {
          return true;
        } else {
          if (value) {
            return true;
          }
          return false;
        }
      }
    ),
  });

  // Handle Submit
  const handleSubmit = (
    values: InstallmentPaymentValues,
    { setSubmitting, resetForm }: FormikHelpers<InstallmentPaymentValues>
  ) => {
    const formattedValues = {
      coupon: values?.couponCode,
      paymentDate: values?.date
        ? moment(values?.date).format("yyyy-MM-DD")
        : "",
      accountId: values?.account?.value,
      mode: values?.paymentMode?.value,
      remark: values?.paymentRemark,
    };

    payInstallment({ body: formattedValues, installmentId }).then(
      (res: any) => {
        if (res?.error) {
          showToast("error", res?.error?.data?.message);
        } else {
          if (res?.data?.status) {
            showToast("success", res?.data?.message);
            resetForm();
            onClose();
          } else {
            showToast("error", res?.data?.message);
          }
        }
        setSubmitting(false);
      }
    );
  };

  return (
    <>
      <Dialog open maxWidth="sm" fullWidth>
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(formikProps) => (
            <Form>
              {isLoading || isFetching ? (
                <ATMFullPageLoader />
              ) : (
                <InstallmentPayment
                  formikProps={formikProps}
                  onClose={onClose}
                  handleCouponApply={(couponCode) => {
                    getCouponDetails({ couponCode, studentId }).then(
                      (res: any) => {
                        if (res?.error) {
                          showToast("error", res?.error?.data?.message);
                        } else {
                          if (res?.data?.status) {
                            showToast("success", res?.data?.message);
                            formikProps.setFieldValue("couponData", {
                              couponCode: res?.data?.data?.couponCode,
                              discountAmount: res?.data?.data?.discountAmount,
                              discountType: res?.data?.data?.amountType,
                              maxDiscountAmount:
                                res?.data?.data?.amountType === "FIXED"
                                  ? formikProps.values?.installmentAmount
                                  : res?.data?.data?.maxDiscountAmount,
                            });
                          } else {
                            showToast("error", res?.data?.message);
                          }
                        }
                      }
                    );
                  }}
                  isCouponDetailsLoading={isCouponDetailsLoading}
                />
              )}
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};

export default InstallmentPaymentWrapper;
